<!-- BulkReassign.vue — Assignments subpage. Filter the active-task universe,
     pick a set of stages/tasks, reassign them to a chosen care manager. -->
<template>
  <div class="p-6 space-y-4">
    <div class="flex items-center gap-2">
      <FontAwesomeIcon :icon="['fas', 'right-left']" class="text-gray-500" />
      <h3 class="text-sm font-semibold text-gray-900">Bulk Reassign</h3>
      <span class="text-xs text-gray-400">Filter active tasks, then reassign them to a care manager.</span>
      <button
        class="ml-auto px-2.5 py-1 text-xs font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded hover:bg-primary-100"
        :disabled="loading"
        @click="loadTasks"
      >
        <FontAwesomeIcon :icon="['fas', loading ? 'spinner' : 'rotate']" :class="{ 'animate-spin': loading }" class="mr-1" />
        Refresh
      </button>
    </div>

    <!-- Filters -->
    <div class="grid grid-cols-2 md:grid-cols-4 gap-3 bg-gray-50 border border-gray-200 rounded-lg p-3">
      <div>
        <label class="block text-[11px] font-medium text-gray-500 mb-1">Program</label>
        <select v-model="filterProgram" class="w-full px-2 py-1.5 text-xs border border-gray-300 rounded">
          <option value="">All programs</option>
          <option v-for="p in programOptions" :key="p.slug" :value="p.slug">{{ p.label }}</option>
        </select>
      </div>
      <div>
        <label class="block text-[11px] font-medium text-gray-500 mb-1">Stage</label>
        <select v-model="filterStage" class="w-full px-2 py-1.5 text-xs border border-gray-300 rounded">
          <option value="">All stages</option>
          <option v-for="s in stageOptions" :key="s.key" :value="s.key">{{ s.label }}</option>
        </select>
      </div>
      <div>
        <label class="block text-[11px] font-medium text-gray-500 mb-1">Current owner</label>
        <select v-model="filterOwner" class="w-full px-2 py-1.5 text-xs border border-gray-300 rounded">
          <option value="">Any owner</option>
          <option value="__unassigned__">Unassigned</option>
          <option v-for="o in ownerOptions" :key="o" :value="o">{{ o }}</option>
        </select>
      </div>
      <div class="flex items-end">
        <label class="flex items-center gap-1.5 text-xs text-gray-600">
          <input v-model="filterOverdue" type="checkbox" class="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
          Overdue only
        </label>
      </div>
    </div>

    <!-- Reassign bar -->
    <div class="flex items-center gap-3 bg-primary-50 border border-primary-200 rounded-lg px-3 py-2">
      <span class="text-xs font-semibold text-primary-700">{{ selectedTaskIds.size }} selected</span>
      <span class="text-xs text-gray-500">of {{ filteredTasks.length }} matching</span>
      <select v-model="targetMemberId" class="ml-auto px-2 py-1.5 text-xs border border-gray-300 rounded min-w-48">
        <option :value="null">Assign to…</option>
        <option v-for="m in members" :key="m.id" :value="m.id">{{ m.name }}</option>
      </select>
      <!-- SCOPE — UNSET BY DEFAULT, exactly as in the patient-queue modal. Covering
           work and handing over a patient are different acts with opposite
           consequences, so the manager states which one. Reassign stays disabled
           until they do. -->
      <select
v-model="reassignScope" class="px-2 py-1.5 text-xs border border-gray-300 rounded min-w-56"
        :class="reassignScope ? 'border-gray-300' : 'border-amber-300 bg-amber-50'">
        <option value="">What are you reassigning?…</option>
        <option value="stages_only">These stages only — covering the work</option>
        <option value="whole_case">Hand over the patient — transfer ownership</option>
      </select>
      <button
        :disabled="!targetMemberId || !reassignScope || !selectedTaskIds.size || reassigning"
        class="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded hover:bg-primary-700 disabled:opacity-50"
        @click="executeReassign"
      >
        <FontAwesomeIcon v-if="reassigning" :icon="['fas', 'spinner']" class="animate-spin mr-1" />
        Reassign {{ selectedTaskIds.size }}
      </button>
    </div>

    <!-- What the chosen scope will actually do, in numbers. -->
    <p v-if="selectedTaskIds.size" class="text-xs" :class="reassignScope ? 'text-gray-500' : 'text-amber-700'">
      <template v-if="!reassignScope">Choose what you are reassigning to continue.</template>
      <template v-else-if="reassignScope === 'whole_case'">
        {{ selectedTaskIds.size }} stage{{ selectedTaskIds.size !== 1 ? 's' : '' }} and
        {{ selectedCaseIds.length }} case{{ selectedCaseIds.length !== 1 ? 's' : '' }} will move to the chosen care manager.
        Every future stage on those cases goes to them too.
      </template>
      <template v-else>
        {{ selectedTaskIds.size }} stage{{ selectedTaskIds.size !== 1 ? 's' : '' }} will be reassigned.
        The next stage still goes to the current case owner.
      </template>
    </p>

    <!-- Task list -->
    <div class="border border-gray-200 rounded-lg overflow-hidden">
      <table class="w-full text-xs">
        <thead class="bg-gray-50 text-gray-600">
          <tr>
            <th class="w-8 px-3 py-2 text-left">
              <input type="checkbox" :checked="allFilteredSelected" class="rounded border-gray-300 text-primary-600 focus:ring-primary-500" @change="toggleSelectAll" />
            </th>
            <th class="px-3 py-2 text-left font-medium">Patient</th>
            <th class="px-3 py-2 text-left font-medium">Provider</th>
            <th class="px-3 py-2 text-left font-medium">Program</th>
            <th class="px-3 py-2 text-left font-medium">Stage</th>
            <th class="px-3 py-2 text-left font-medium">Due</th>
            <th class="px-3 py-2 text-left font-medium">Current owner</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100">
          <tr v-if="loading"><td colspan="7" class="px-3 py-8 text-center text-gray-400"><FontAwesomeIcon :icon="['fas', 'spinner']" class="animate-spin" /></td></tr>
          <tr v-else-if="!filteredTasks.length"><td colspan="7" class="px-3 py-8 text-center text-gray-400">No matching tasks.</td></tr>
          <tr
            v-for="t in filteredTasks"
            v-else
            :key="t.id"
            class="hover:bg-gray-50 cursor-pointer"
            :class="{ 'bg-primary-50/50': selectedTaskIds.has(t.id) }"
            @click="toggleSelect(t.id)"
          >
            <td class="px-3 py-2"><input type="checkbox" :checked="selectedTaskIds.has(t.id)" class="rounded border-gray-300 text-primary-600 focus:ring-primary-500" @click.stop="toggleSelect(t.id)" /></td>
            <td class="px-3 py-2 sensitive text-gray-900">{{ t.patient_name }}</td>
            <td class="px-3 py-2 sensitive" :class="t.provider_name ? 'text-gray-600' : 'text-gray-400 italic'">
              <span class="sensitive" data-spoof-kind="provider">{{ t.provider_name || '—' }}</span> <span v-if="t.practice_name" class="block text-[10px] text-gray-400 truncate sensitive" data-spoof-kind="practice">{{ t.practice_name }}</span>
            </td>
            <td class="px-3 py-2 text-gray-600">{{ t.metric_name }}</td>
            <td class="px-3 py-2 text-gray-600">{{ t.task_type }}</td>
            <td class="px-3 py-2" :class="t.is_overdue ? 'text-red-600 font-medium' : 'text-gray-500'">{{ t.due_date || '—' }}</td>
            <td class="px-3 py-2 sensitive" :class="t.owner_name ? 'text-gray-600' : 'text-gray-400 italic'">{{ t.owner_name || 'Unassigned' }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { fetchPatientWorkQueue } from '@/services/api/patientWorkQueue';

export default {
  name: 'BulkReassign',
  components: { FontAwesomeIcon },

  data() {
    return {
      loading: false,
      reassigning: false,
      tasks: [],
      members: [],
      filterProgram: '',
      filterStage: '',
      filterOwner: '',
      filterOverdue: false,
      selectedTaskIds: new Set(),
      targetMemberId: null,
      // Reassign scope: '' | 'stages_only' | 'whole_case'. No default — the user
      // must say whether they are covering work or handing over patients. Same
      // contract as the patient-queue modal.
      reassignScope: '',
    };
  },

  computed: {
    // Distinct opportunities behind the selected tasks — the cases that change
    // hands under 'whole_case'. One case can carry several stages, so this is
    // deliberately not the task count.
    selectedCaseIds() {
      const s = new Set();
      for (const t of this.tasks) {
        if (this.selectedTaskIds.has(t.id) && t.opportunity_id) s.add(t.opportunity_id);
      }
      return Array.from(s);
    },
    programOptions() {
      const m = new Map();
      for (const t of this.tasks) if (t.metric_slug && !m.has(t.metric_slug)) m.set(t.metric_slug, t.metric_name || t.metric_slug);
      return Array.from(m, ([slug, label]) => ({ slug, label })).sort((a, b) => a.label.localeCompare(b.label));
    },
    stageOptions() {
      const m = new Map();
      for (const t of this.tasks) {
        if (this.filterProgram && t.metric_slug !== this.filterProgram) continue;
        const key = String(t.task_type_id);
        if (!m.has(key)) m.set(key, { key, label: t.task_type || `Stage ${key}` });
      }
      return Array.from(m.values()).sort((a, b) => a.label.localeCompare(b.label));
    },
    ownerOptions() {
      const s = new Set();
      for (const t of this.tasks) if (t.owner_name) s.add(t.owner_name);
      return Array.from(s).sort();
    },
    filteredTasks() {
      return this.tasks.filter(t => {
        if (this.filterProgram && t.metric_slug !== this.filterProgram) return false;
        if (this.filterStage && String(t.task_type_id) !== this.filterStage) return false;
        if (this.filterOwner === '__unassigned__' && t.owner_name) return false;
        if (this.filterOwner && this.filterOwner !== '__unassigned__' && t.owner_name !== this.filterOwner) return false;
        if (this.filterOverdue && !t.is_overdue) return false;
        return true;
      });
    },
    allFilteredSelected() {
      return this.filteredTasks.length > 0 && this.filteredTasks.every(t => this.selectedTaskIds.has(t.id));
    },
  },

  mounted() {
    this.loadMembers();
    this.loadTasks();
  },

  methods: {
    async loadMembers() {
      try {
        const { data } = await this.$axios.get('/api/team-assignments/summary/');
        const rows = Array.isArray(data) ? data : (data.members || []);
        this.members = rows.map(m => ({ id: m.member_id, name: m.member__name || m.member_name }))
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      } catch (e) {
        console.error('Error loading members:', e);
      }
    },

    async loadTasks() {
      this.loading = true;
      this.selectedTaskIds = new Set();
      try {
        // Reuse the patient work queue, flatten to one row per active task.
        const data = await fetchPatientWorkQueue({
          start_row: 0,
          end_row: 999999,
          filter_args: { metric_ids: [], show_completed: false, include_snoozed: true },
        });
        const rows = data.rowData || [];
        const flat = [];
        for (const p of rows) {
          for (const t of (p.tasks || [])) {
            if (![1, 4, 7].includes(t.status_id)) continue;
            flat.push({
              id: t.id,
              patient_name: p.patient_name,
              // Provider + practice for context so a manager can route the
              // reassignment correctly (req 1874). Queue exposes these per patient.
              provider_name: p.pcp_name || null,
              practice_name: p.practice_name || null,
              metric_slug: t.metric_slug,
              metric_name: t.metric_name,
              task_type: t.task_type,
              task_type_id: t.task_type_id,
              // Required for the 'whole_case' scope — transferring ownership needs
              // the opportunity, not just the task. Previously dropped here.
              opportunity_id: t.opportunity_id,
              due_date: t.due_date,
              is_overdue: t.is_overdue,
              owner_name: (t.owners && t.owners.length) ? t.owners[0].name : null,
            });
          }
        }
        this.tasks = flat;
      } catch (e) {
        console.error('Error loading tasks:', e);
        this.tasks = [];
      } finally {
        this.loading = false;
      }
    },

    toggleSelect(id) {
      const next = new Set(this.selectedTaskIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      this.selectedTaskIds = next;
    },
    toggleSelectAll() {
      if (this.allFilteredSelected) {
        const next = new Set(this.selectedTaskIds);
        for (const t of this.filteredTasks) next.delete(t.id);
        this.selectedTaskIds = next;
      } else {
        const next = new Set(this.selectedTaskIds);
        for (const t of this.filteredTasks) next.add(t.id);
        this.selectedTaskIds = next;
      }
    },

    async executeReassign() {
      // Member AND explicit scope both required — there is no default scope.
      if (!this.targetMemberId || !this.reassignScope || !this.selectedTaskIds.size) return;
      this.reassigning = true;
      try {
        // 'whole_case' ALSO sends opportunity_ids, which transfers durable case
        // ownership so future stages follow the new person. Stages-only omits it,
        // leaving the existing owner in place for the next stage.
        const payload = {
          member_id: this.targetMemberId,
          task_ids: Array.from(this.selectedTaskIds),
          replace: true,
        };
        if (this.reassignScope === 'whole_case') payload.opportunity_ids = this.selectedCaseIds;
        const { data } = await this.$axios.post('/api/team-assignments/bulk-assign/', payload);
        const nT = data?.tasks ?? this.selectedTaskIds.size;
        const nC = data?.cases ?? 0;
        this.$root.$emit('show-success-toast',
          this.reassignScope === 'whole_case'
            ? `Reassigned ${nT} stage${nT !== 1 ? 's' : ''} and handed over ${nC} case${nC !== 1 ? 's' : ''}`
            : `Reassigned ${nT} stage${nT !== 1 ? 's' : ''}`);
        this.targetMemberId = null;
        this.reassignScope = '';
        await this.loadTasks();
      } catch (e) {
        console.error('Error reassigning:', e);
        this.$root.$emit('show-error-toast', 'Failed to reassign');
      } finally {
        this.reassigning = false;
      }
    },
  },
};
</script>

<style scoped>
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
.animate-spin { animation: spin 1s linear infinite; }
</style>
